import React, { useEffect, useState, useRef } from "react";
import { Monitor, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Non-blocking amber banner shown when the current user has ≥ 2 sessions
 * active in the last 5 minutes.
 *
 * We poll once on mount and then every 60s — light-weight ($in lookup against
 * an indexed collection). Dismissable for this tab via in-memory state.
 */
const POLL_INTERVAL_MS = 60_000;

const SessionsWarningBanner = ({ className = "" }) => {
  const [count, setCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/auth/active-sessions");
        if (!cancelled) setCount(r.data?.count || 0);
      } catch {
        // Silent — banner just won't show if endpoint is unavailable.
      }
    };
    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (dismissed || count < 2) return null;

  return (
    <div
      data-testid="sessions-warning-banner"
      className={`flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50 ${className}`}
    >
      <Monitor className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-amber-900">
          This account is signed in on {count} devices
        </p>
        <p className="text-[11px] text-amber-700 mt-0.5">
          Multiple active sessions can slow things down for everyone. Consider
          signing out from the device you're not using.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="sessions-warning-dismiss"
        className="text-amber-700 hover:text-amber-900 p-1"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default SessionsWarningBanner;
